import type { ConversationsUnavailableReason } from "posthog-js";
import type { AnalyticsClient } from "@/lib/analytics/analytics-client";
import type { PostHog, PostHogSdk } from "@/lib/analytics/posthog-sdk";
import { formatFeedbackMessage } from "./feedback-message";
import type {
  Feedback,
  Reporter,
  SubmitResult,
  SupportClient,
} from "./support-client";

/**
 * The reasons PostHog gives while it is still finding out. Support is switched
 * on in the project's settings, which the SDK fetches after `init`, and then
 * loads a bundle of its own; until both arrive the answer is "not yet", which
 * must not be taken for "no". Every other reason is final for the visit.
 */
const STILL_ARRIVING: ReadonlySet<ConversationsUnavailableReason> = new Set([
  "remote_config_pending",
  "initializing",
  "not_loaded",
]);

/**
 * How often to ask again, and for how long. The SDK offers no callback for
 * the settings arriving, so the question is repeated; the bound is what stops
 * a blocked or hung request from keeping the answer open for the visit.
 */
const ASK_EVERY_MS = 250;
const GIVE_UP_AFTER_MS = 10_000;

/**
 * How long a send may take before the form is told it could not be confirmed.
 * While the project is over its event quota the SDK drops the message without
 * settling its promise, so with no bound the form would wait for the rest of
 * the visit. A send that is only slow can still make a ticket after this.
 */
const SEND_GIVE_UP_AFTER_MS = 15_000;

const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Kept apart from the SDK's own `null`, which is a definite no. */
const GIVEN_UP = Symbol("given up");

/** {@link GIVEN_UP} when the send has not settled within `ms`. */
async function settledWithin<T>(sending: Promise<T>, ms: number) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const givenUp = new Promise<typeof GIVEN_UP>((resolve) => {
    timer = setTimeout(() => resolve(GIVEN_UP), ms);
  });
  try {
    return await Promise.race([sending, givenUp]);
  } finally {
    clearTimeout(timer);
  }
}

/** `undefined` while PostHog is still finding out. */
function answerSoFar(posthog: PostHog): boolean | undefined {
  const reason = posthog.conversations.getUnavailableReason();
  if (reason === null) return true;
  return STILL_ARRIVING.has(reason) ? undefined : false;
}

async function settledAnswer(posthog: PostHog): Promise<boolean> {
  for (let waited = 0; waited < GIVE_UP_AFTER_MS; waited += ASK_EVERY_MS) {
    const answer = answerSoFar(posthog);
    if (answer !== undefined) return answer;
    await wait(ASK_EVERY_MS);
  }
  return answerSoFar(posthog) ?? false;
}

/**
 * Adapts PostHog Support to {@link SupportClient}, over the SDK instance
 * analytics already uses. A ticket that is made is also reported to
 * analytics, by kind and id only: the words stay in the helpdesk.
 */
export function createPostHogSupport(
  sdk: PostHogSdk,
  analytics: AnalyticsClient,
): SupportClient {
  // The SDK once Support has said yes, or `null`. A yes is kept for the
  // visit. A no is asked again next time: settings that were slow, failed, or
  // had Support off may have changed since.
  let ready: Promise<PostHog | null> | null = null;
  const whenReady = () => {
    ready ??= sdk
      .load()
      .then(async (posthog) =>
        posthog && (await settledAnswer(posthog)) ? posthog : null,
      )
      .catch(() => null)
      .then((posthog) => {
        if (!posthog) ready = null;
        return posthog;
      });
    return ready;
  };

  return {
    isAvailable: () => whenReady().then(Boolean),

    async submit(
      feedback: Feedback,
      reporter: Reporter,
    ): Promise<SubmitResult> {
      // The button is drawn whether or not Support is on, so the answer is
      // taken here, before anything is sent.
      const posthog = await whenReady();
      if (!posthog) return { ok: false, reason: "unavailable" };

      try {
        // No traits: PostHog is to hold no email address, so the sender is
        // named by the HubSpot contact line in the message. Without traits
        // the SDK reads a name and email from the person properties identify
        // set, and analytics sets neither.
        //
        // `true` makes every submit a ticket of its own. Without it the SDK
        // appends to the last ticket this browser opened, which would file a
        // feature idea under an old bug report. The SDK also records the page
        // URL, query string included, on each new ticket. That is a request of
        // its own, not a captured event, so `before_send` does not strip it.
        const sending = posthog.conversations
          .sendMessage(
            formatFeedbackMessage(feedback, reporter),
            undefined,
            true,
          )
          .then((sent) => {
            // Reported whenever it is made, so a ticket that arrives after
            // the form was told is still counted.
            if (sent) {
              analytics.capture("feedback_submitted", {
                kind: feedback.kind,
                ticket_id: sent.ticket_id,
              });
            }
            return sent;
          });
        const sent = await settledWithin(sending, SEND_GIVE_UP_AFTER_MS);
        if (sent === GIVEN_UP) return { ok: false, reason: "timed_out" };
        // `null` is the SDK saying Support is not loaded, which a sign-out in
        // this tab can cause after the button was shown.
        if (!sent) return { ok: false, reason: "failed" };

        return { ok: true, ticketId: sent.ticket_id };
      } catch {
        // The form keeps the text and says it failed; what went wrong is
        // already in the SDK's own log.
        return { ok: false, reason: "failed" };
      }
    },
  };
}
